/**
 * API Key Service — Manages external API keys for the Developer Portal.
 *
 * Dashboard endpoints (authenticated via session cookie / JWT):
 *   GET    /api-keys/scopes         — Available scopes + descriptions
 *   POST   /api-keys/generate       — Create a new API key (returns key_id + api_secret)
 *   GET    /api-keys/list           — List all keys (key_id visible, secret never returned)
 *   PUT    /api-keys/:id            — Update key settings
 *   PUT    /api-keys/:id/revoke     — Revoke a key permanently
 *   PUT    /api-keys/:id/rotate     — Rotate (regenerate) a key secret
 *   PUT    /api-keys/:id/allowed-ips — Update IP whitelist for a key
 *   GET    /api-keys/audit-logs     — Paginated audit trail
 *   GET    /api-keys/security-events — Security events (auth failures, IP blocks)
 *
 * Backend response format: { status: bool, statuscode: number, message: string, entity: ... }
 */
import axiosConfig from '../config/axiosConfig';

/** Safely extract string error message from any response shape */
const extractError = (res) => {
  if (typeof res?.message === 'string') return res.message;
  if (typeof res?.error === 'string') return res.error;
  if (typeof res?.error?.message === 'string') return res.error.message;
  return 'Something went wrong';
};

const ApiKeyService = {
  /**
   * Get available scopes and their descriptions.
   * GET /api-keys/scopes
   */
  getScopes: async () => {
    const response = await axiosConfig.get('/api-keys/scopes');
    const d = response.data;
    return {
      success: d.status !== false && d.statuscode < 400,
      entity: d.entity,
      message: d.message,
    };
  },

  /**
   * Generate a new API key.
   * POST /api-keys/generate
   * Body: { keyName, scopes, rateLimitPerMinute?, expiresAt?, webhookUrl?, webhookEvents? }
   *
   * ⚠️ entity.key_id and entity.api_secret are only returned ONCE.
   */
  generateKey: async (payload) => {
    const response = await axiosConfig.post('/api-keys/generate', payload);
    const d = response.data;
    return {
      success: d.status !== false && d.statuscode < 400,
      entity: d.entity,
      message: d.message,
      error: extractError(d),
    };
  },

  /**
   * List all API keys (key_id visible, secret never returned).
   * GET /api-keys/list
   * Returns entity: array of key objects
   */
  listKeys: async () => {
    const response = await axiosConfig.get('/api-keys/list');
    const d = response.data;
    return {
      success: d.status !== false && d.statuscode < 400,
      entity: d.entity,
      message: d.message,
    };
  },

  /**
   * Update an API key's settings (name, scopes, rate limit, webhook).
   * PUT /api-keys/:id
   * Body: any subset of { keyName, scopes, rateLimitPerMinute, webhookUrl, webhookEvents }
   */
  updateKey: async (id, payload) => {
    const response = await axiosConfig.put(`/api-keys/${id}`, payload);
    const d = response.data;
    return {
      success: d.status !== false && d.statuscode < 400,
      entity: d.entity,
      message: d.message,
      error: extractError(d),
    };
  },

  /**
   * Revoke an API key permanently.
   * PUT /api-keys/:id/revoke
   * Body: { reason?: string }
   */
  revokeKey: async (id, reason) => {
    const body = reason ? { reason } : {};
    const response = await axiosConfig.put(`/api-keys/${id}/revoke`, body);
    const d = response.data;
    return {
      success: d.status !== false && d.statuscode < 400,
      entity: d.entity,
      message: d.message,
      error: extractError(d),
    };
  },

  /**
   * Rotate an API key — revokes old secret, creates new with same settings.
   * PUT /api-keys/:id/rotate
   *
   * ⚠️ entity.api_secret is only returned ONCE.
   */
  rotateKey: async (id) => {
    const response = await axiosConfig.put(`/api-keys/${id}/rotate`);
    const d = response.data;
    return {
      success: d.status !== false && d.statuscode < 400,
      entity: d.entity,
      message: d.message,
      error: extractError(d),
    };
  },

  /**
   * Get paginated audit logs.
   * GET /api-keys/audit-logs?page=1&limit=50
   */
  getAuditLogs: async ({ page = 1, limit = 20 } = {}) => {
    const response = await axiosConfig.get('/api-keys/audit-logs', {
      params: { page, limit },
    });
    const d = response.data;
    return {
      success: d.status !== false && d.statuscode < 400,
      entity: d.entity,
      message: d.message,
    };
  },

  /**
   * Update IP whitelist for a specific key.
   * PUT /api-keys/:id/allowed-ips
   * Body: { allowed_ips: string[] }
   */
  updateAllowedIps: async (id, allowedIps) => {
    const response = await axiosConfig.put(`/api-keys/${id}/allowed-ips`, {
      allowed_ips: allowedIps,
    });
    const d = response.data;
    return {
      success: d.status !== false && d.statuscode < 400,
      entity: d.entity,
      message: d.message,
      error: extractError(d),
    };
  },

  /**
   * Get security events (auth failures, IP blocks, key disablements).
   * GET /api-keys/security-events?page=1&limit=20
   */
  getSecurityEvents: async ({ page = 1, limit = 20 } = {}) => {
    const response = await axiosConfig.get('/api-keys/security-events', {
      params: { page, limit },
    });
    const d = response.data;
    return {
      success: d.status !== false && d.statuscode < 400,
      entity: d.entity,
      message: d.message,
    };
  },

  /**
   * Get usage statistics for all API keys.
   * GET /api-keys/usage-stats?days=30
   */
  getUsageStats: async ({ days = 30 } = {}) => {
    const response = await axiosConfig.get('/api-keys/usage-stats', {
      params: { days },
    });
    const d = response.data;
    return {
      success: d.status !== false && d.statuscode < 400,
      entity: d.entity,
      message: d.message,
    };
  },
};

export default ApiKeyService;
